import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import type { Controls, EventData, TooltipRenderProps } from 'react-joyride';
import { getConstructionPriceEstimatorTourSteps, type ConstructionEstimatorTourState } from '../../stages/ConstructionPriceEstimator.tour';
import { TrackedJoyride } from './TrackedJoyride';
import TourFooter from './TourFooter';
import { TourHintContext, useTourHint } from './TourHintContext';
import { getTourJoyride } from './tourJoyrideStyles';
import { useTourSeen } from './useTourSeen';

export interface ConstructionPriceEstimatorTourHandle { replay(): void; }

/** Keep keyboard navigation inside the displayed guidance, not its launcher. */
export function EstimatorTourTooltip(props: TooltipRenderProps) {
  const root = useRef<HTMLDivElement>(null);
  const { isHintMode } = useTourHint();
  useEffect(() => {
    if (isHintMode) return;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    const frame = requestAnimationFrame(() => {
      const buttons = root.current?.querySelectorAll<HTMLButtonElement>('button');
      buttons?.[buttons.length - 1]?.focus({ preventScroll: true });
      // Keyboard transitions can preserve the previous focus/scroll position
      // while Floating UI settles. Reframe the final bubble once, only if it
      // falls outside the viewport; do not keep scrolling during user edits.
      settleTimer = setTimeout(() => {
        const bubble = root.current?.querySelector<HTMLElement>('[role="alertdialog"]');
        const bounds = bubble?.getBoundingClientRect();
        if (bounds && (bounds.top < 0 || bounds.bottom > window.innerHeight)) {
          bubble?.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: 'auto' });
        }
      }, 100);
    });
    return () => { cancelAnimationFrame(frame); if (settleTimer) clearTimeout(settleTimer); };
  }, [props.index, isHintMode]);
  const Footer = TourFooter as unknown as React.ComponentType<TooltipRenderProps>;
  return <div ref={root}><Footer {...props} /></div>;
}

/** Manual walkthrough; pause while input/result anchors are temporarily hidden. */
const ConstructionPriceEstimatorTour = forwardRef<ConstructionPriceEstimatorTourHandle, ConstructionEstimatorTourState & { busy: boolean }>(
  ({ standalone, detailsReady, resultsReady, downloadReady = false, busy, canEdit = true }, ref) => {
    const theme = useTheme();
    const { state, lastIndex, markPhaseAComplete, markSeen, reset } = useTourSeen('price-realism-estimator');
    const [requested, setRequested] = useState(false);
    const [showResumeHint, setShowResumeHint] = useState(false);
    const [startIndex, setStartIndex] = useState(0);
    const [replayNonce, setReplayNonce] = useState(0);
    const currentIndexRef = useRef(0);
    const currentTargetRef = useRef<string | null>(null);
    const controlsRef = useRef<Controls | null>(null);
    const steps = useMemo(() => getConstructionPriceEstimatorTourSteps({ standalone, detailsReady, resultsReady, downloadReady, canEdit }), [standalone, detailsReady, resultsReady, downloadReady, canEdit]);
    const run = requested && !busy;

    // Dynamic panels may replace a step during an actual job. Resume at the
    // same semantic target when possible, instead of a now-unrelated index.
    useEffect(() => {
      if (!requested) return;
      const matchingIndex = steps.findIndex(step => step.target === currentTargetRef.current);
      const nextIndex = matchingIndex >= 0 ? matchingIndex : Math.min(currentIndexRef.current, steps.length - 1);
      setStartIndex(nextIndex);
      setReplayNonce(value => value + 1);
    }, [busy, requested, steps]);

    const requestExit = useCallback(() => {
      markPhaseAComplete(currentIndexRef.current);
      setShowResumeHint(true);
    }, [markPhaseAComplete]);
    const dismissHint = useCallback(() => { setShowResumeHint(false); setRequested(false); }, []);
    const handleEvent = useCallback((data: EventData, controls?: Controls) => {
      if (controls) controlsRef.current = controls;
      if (data.type === EVENTS.STEP_BEFORE && typeof data.index === 'number') {
        currentIndexRef.current = data.index;
        currentTargetRef.current = typeof steps[data.index]?.target === 'string' ? steps[data.index].target as string : null;
        // Floating UI positions the tooltip after TourFooter's first layout
        // effect. Bring an offscreen target into view once on step entry;
        // tall sections use their tiny top anchors rather than their center.
        if (currentTargetRef.current) {
          document.querySelector(currentTargetRef.current)?.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: 'auto' });
        }
      }
      if (data.status === STATUS.SKIPPED || (data.action === ACTIONS.CLOSE && data.type === EVENTS.TOUR_END)) requestExit();
      else if (data.status === STATUS.FINISHED && data.type === EVENTS.TOUR_END) {
        markSeen();
        setRequested(false);
      }
    }, [markSeen, requestExit, steps]);

    useImperativeHandle(ref, () => ({ replay: () => {
      const nextIndex = state === 'phaseB-pending' && lastIndex !== null && lastIndex < steps.length ? lastIndex : 0;
      if (nextIndex === 0) reset();
      currentIndexRef.current = nextIndex;
      currentTargetRef.current = steps[nextIndex].target as string;
      setStartIndex(nextIndex);
      setReplayNonce(value => value + 1);
      setShowResumeHint(false);
      setRequested(true);
    } }), [lastIndex, reset, state, steps]);

    useEffect(() => {
      if (!run) return;
      const onKeyDown = (event: KeyboardEvent) => {
        // Do not steal arrows used to edit a text field or select an option.
        const target = event.target as HTMLElement | null;
        if (target instanceof Element && target.closest('input, textarea, select, [role="combobox"], [contenteditable="true"]')) return;
        if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Escape'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        if (event.key === 'Escape') { if (showResumeHint) dismissHint(); else requestExit(); }
        else if (!showResumeHint) {
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') controlsRef.current?.next?.();
          else controlsRef.current?.prev?.();
        }
      };
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }, [dismissHint, requestExit, run, showResumeHint]);

    const hint = useMemo(() => ({ isHintMode: showResumeHint, onRequestExit: requestExit, onDismissHint: dismissHint }), [showResumeHint, requestExit, dismissHint]);
    const joyride = useMemo(() => getTourJoyride(theme, { zIndex: 2300, hideSpotlight: showResumeHint }), [theme, showResumeHint]);
    return <TourHintContext.Provider value={hint}><TrackedJoyride
      tourKey="price-realism-estimator"
      key={`price-tour-${startIndex}-${replayNonce}`}
      steps={steps} run={run} initialStepIndex={startIndex} continuous locale={{ last: 'Done' }}
      tooltipComponent={EstimatorTourTooltip}
      onEvent={handleEvent} options={joyride.options} styles={joyride.styles}
    /></TourHintContext.Provider>;
  },
);
ConstructionPriceEstimatorTour.displayName = 'ConstructionPriceEstimatorTour';
export default ConstructionPriceEstimatorTour;
